import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import {
  GAME_EMBEDDED_TITLES,
  GameInteractions,
} from '../../../components/game-info/GameInteractions';
import { GameDescription } from '../../../components/game-info/GameDescription';
import { GameMediaBanner } from '../../../components/game-info/GameMediaBanner';
// import { GamePlayersOnline } from '../../../components/game-info/GamePlayersOnline';
import { GameTitle } from '../../../components/game-info/GameTitle';
import { TwoColumnLayout } from '../../../components/TwoColumnLayout';
import { GameAnnouncements } from '../../../components/game-announcements/GameAnnouncements';
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { getAbsoluteHeight } from '../../../util/DOM';
import { GameTags } from '../../../components/game-info/GameTags';
import { Game } from 'functions/src/types/firestore/Game';
import { useRouter } from 'next/router';
import { getGameTabs } from '../../../util/game';
import { toKebabCase } from '../../../util/string';
import { GameSocialFeed } from '../../../components/social-feed/GameSocialFeed';
import { Social } from 'functions/src/types/Social';
import { DownloadGameDialog } from '../../../components/game-download/DownloadGameDialog';
import { DOWNLOAD_DIALOG_PROPS } from '../../../util/game/downloadDialogue';
import { Announcement } from 'functions/src/types/firestore/Game/Announcement';
import { updateCurrentGame } from 'src/firebaseCloud';
import { useAuth } from 'src/contexts/AuthContext';
import { useMobile } from 'src/hooks/useMobile';
import { useMedia } from 'src/contexts/MediaContext';

const GAME_ANNOUNCEMENTS_TO_SHOW = 3; // The number of stacked game announcements to show before adding a scrollbara

type GameOverviewPageProps = {
  openTabName: string;
  announcements: Announcement<Date>[];
  game: Game<Date>;
};

function GameOverviewPage({
  openTabName,
  game,
  announcements,
}: GameOverviewPageProps) {
  const { user } = useAuth();
  const { media: gameMedia } = useMedia();
  const [isDownloadGameDialogOpen, setIsDownloadGameDialogOpen] =
    useState(false);
  const [isGamePlaying, setIsGamePlaying] = useState(false);
  const [firstGamePlay, setFirstGamePlay] = useState(true);
  const router = useRouter();
  const tabName = openTabName || 'Items';
  useEffect(() => {
    if (firstGamePlay && isGamePlaying) {
      setFirstGamePlay(false);
    }
  }, [isGamePlaying, firstGamePlay]);
  const [gameTabs, setGameTabs] = useState<ReactElement>(<></>);
  const [leaderboardFromQuery, setLeaderboardFromQuery] = useState({
    leaderboard: '',
    set: false,
  });
  const { leaderboard } = router.query;
  useEffect(() => {
    if (!router?.isReady) {
      return;
    }
    if (!leaderboardFromQuery.set && leaderboard) {
      setLeaderboardFromQuery({
        leaderboard: leaderboard as string,
        set: true,
      });
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leaderboard, router?.isReady]);
  useEffect(() => {
    if (game && router?.isReady) {
      router.push(
        {
          pathname: `/game/${toKebabCase(game?.title)}/${toKebabCase(tabName)}`,
          query: router.query,
        },
        undefined,
        { shallow: true },
      );
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.title, tabName, router?.isReady]);

  useEffect(() => {
    if (game) {
      setGameTabs(
        getGameTabs(
          game,
          openTabName,
          leaderboardFromQuery.leaderboard,
        ) as ReactElement,
      );
    }
  }, [game, openTabName, leaderboardFromQuery]);
  const gameDetailsLeftWrapperRef = useRef<HTMLDivElement>();
  const gameDetailsRightWrapperRef = useRef<HTMLDivElement>();

  const adjustGameDetailsColumnHeights = (): void => {
    if (!!gameDetailsRightWrapperRef.current) {
      // Adjust the max height of the game announcements stack to show
      // precisely up to the first gameAnnouncementsToShow announcement rows
      const gameRightSideOuterStack: HTMLElement =
        gameDetailsRightWrapperRef.current.querySelector<HTMLElement>(
          'div[class*="MuiStack"]',
        ) as HTMLElement;

      if (
        gameRightSideOuterStack &&
        gameRightSideOuterStack.children.length > 1
      ) {
        const gameAnnouncementsStack: HTMLElement =
          gameRightSideOuterStack.children[1].querySelector<HTMLElement>(
            'div[class*="MuiStack"]',
          ) as HTMLElement;
        const gameAnnouncements = gameAnnouncementsStack.children;

        if (gameAnnouncements && gameAnnouncements.length > 0) {
          let maxHeight = 0;
          for (let i = 0; i < GAME_ANNOUNCEMENTS_TO_SHOW; i++) {
            // If we run out of announcements to use the height of then just reuse the height of the last element
            const currentAnnouncement: Element =
              gameAnnouncements[Math.min(gameAnnouncements.length - 1, i)];
            maxHeight += (currentAnnouncement as HTMLElement).offsetHeight;
          }

          maxHeight -= GAME_ANNOUNCEMENTS_TO_SHOW; // For the bottom border to not visible

          gameAnnouncementsStack.style.maxHeight = `${maxHeight}px`;
        }
      }

      if (gameDetailsLeftWrapperRef.current !== undefined) {
        // Make sure the left column doesn't exceed the right column in height
        const rightColumnWrapper: HTMLCollection =
          gameDetailsRightWrapperRef.current.children[0]?.children;

        if (rightColumnWrapper && rightColumnWrapper.length > 1) {
          let rightHeight = 0;

          for (let i = 0; i < rightColumnWrapper.length; i++) {
            rightHeight += getAbsoluteHeight(
              rightColumnWrapper[parseInt(`${i}`)] as HTMLElement,
            );
          }
          gameDetailsLeftWrapperRef.current.style.maxHeight = `${rightHeight}px`;
        }
      }
    }
  };

  useEffect(() => {
    adjustGameDetailsColumnHeights();
    window.addEventListener('resize', adjustGameDetailsColumnHeights);
    return () => {
      window.removeEventListener('resize', adjustGameDetailsColumnHeights);
    };
  }, [gameDetailsLeftWrapperRef, gameDetailsRightWrapperRef]);

  const isMobile = useMobile();

  const getMobileOperatingSystem = () => {
    if (typeof window === 'undefined') {
      return 'unknown';
    }
    const userAgent = navigator?.userAgent || navigator?.vendor;

    // Windows Phone must come first because its UA also contains "Android"
    if (/windows phone/i.test(userAgent)) {
      return 'Windows Phone';
    }

    if (/android/i.test(userAgent)) {
      return 'Android';
    }

    // iOS detection from: http://stackoverflow.com/a/9039885/177710
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return 'iOS';
    }

    return 'unknown';
  };

  const gameUrl = useMemo(() => {
    const operatingSystem = getMobileOperatingSystem();
    return operatingSystem === 'Android' && game?.gameUrl?.android
      ? game?.gameUrl?.android
      : operatingSystem === 'iOS' && game?.gameUrl?.apple
      ? game?.gameUrl?.apple
      : operatingSystem === 'Windows Phone' && game?.gameUrl?.android
      ? game?.gameUrl?.android
      : game?.gameUrl?.generic;
  }, [game?.gameUrl]);
  const GameInteractionsAndAnnouncements = useMemo(() => {
    return (
      <Stack
        spacing={4}
        direction={'column'}
        justifyContent={'center'}
        alignItems={'stretch'}
      >
        <GameInteractions
          isGamePlaying={isGamePlaying}
          gameUrl={gameUrl}
          gameEmbedded={!!game?.unityUrl}
          playGameFn={
            game
              ? game.title === 'Undead Blocks'
                ? () => {
                    return setIsDownloadGameDialogOpen(true);
                  }
                : GAME_EMBEDDED_TITLES.includes(game.title)
                ? async () => {
                    //scroll to top
                    window.scrollTo(0, 0);
                    setIsGamePlaying((prev) => {
                      return !prev;
                    });
                    if (user?.uid) {
                      try {
                        await updateCurrentGame({
                          gameId: game.id,
                          playing: !isGamePlaying,
                          uid: user.uid,
                        });
                      } catch (error) {
                        console.error(error);
                      }
                    }
                  }
                : async () => {
                    return undefined;
                  }
              : async () => {
                  return undefined;
                }
          }
          mintNowFn={
            game && game.title === 'Undead Blocks'
              ? () => {
                  return window.open(
                    'https://www.undeadblocks.com/staking',
                    '_blank',
                  );
                }
              : undefined
          }
          linkedSocials={(game?.socials || []) as Social[]}
          distributeButtonsProps={{
            shareOnClick: () => {
              return console.log('TODO share');
            },
            notificationOnClick: () => {
              return console.log('TODO subscribe notifications');
            },
          }}
        />
        <GameAnnouncements announcements={announcements} />
      </Stack>
    );
  }, [announcements, game, gameUrl, isGamePlaying, user?.uid]);

  const gameMediaWidth = isMobile ? '100%' : 'calc(100% - 450px)';

  const GameNoninteractive = useMemo(() => {
    return (
      <Box>
        <Stack spacing={2}>
          <GameTitle platforms={game?.platforms} title={game?.title} />
          {/* <GamePlayersOnline playerCount={129} /> */}
          <Box
            sx={{
              py: 1,
            }}
          >
            <GameTags tags={game?.tags} />
          </Box>
          <GameDescription description={game?.description} />
        </Stack>
      </Box>
    );
  }, [game?.description, game?.platforms, game?.tags, game?.title]);

  return (
    <>
      {game ? (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {gameMedia.length > 0 ? (
              <Box
                sx={{
                  width: `${gameMediaWidth}`,
                }}
              >
                <GameMediaBanner
                  gamePlayOptions={{ isGamePlaying, firstGamePlay }}
                  unityOptions={{
                    unityUrl: game?.unityUrl || '',
                    unityEnabled: GAME_EMBEDDED_TITLES.includes(game?.title),
                  }}
                />
              </Box>
            ) : (
              ''
            )}
            {isMobile ? (
              ''
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  minHeight: 'calc(100% - 32px)',
                  ml: 0,
                  mt: 0,
                }}
              >
                <GameSocialFeed
                  twitter={
                    game?.socials?.filter((social) => {
                      return social.name === 'twitter';
                    })[0]
                  }
                />
              </Box>
            )}
          </Box>

          <Box
            mx={6}
            mt={4}
            sx={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {isMobile ? (
              <>
                {GameNoninteractive}
                {GameInteractionsAndAnnouncements}
              </>
            ) : (
              <TwoColumnLayout
                leftWrapperRef={gameDetailsLeftWrapperRef}
                rightWrapperRef={gameDetailsRightWrapperRef}
                spaceBetween={8}
                left={GameNoninteractive}
                right={GameInteractionsAndAnnouncements}
              />
            )}
          </Box>
          <Box sx={{ mt: { xs: 11, lg: 8 } }}>{game ? gameTabs : ''}</Box>
          {game && game.title === 'Undead Blocks' && (
            <DownloadGameDialog
              open={isDownloadGameDialogOpen}
              onClose={() => {
                return setIsDownloadGameDialogOpen(false);
              }}
              title={game.title}
              recRequirements={
                DOWNLOAD_DIALOG_PROPS['Undead Blocks'].recRequirements
              }
              version={
                game.gameVersions.sort((versionA, versionB) => {
                  return versionB.date.getTime() - versionA.date.getTime();
                })[0].version
              }
            />
          )}
        </>
      ) : (
        ''
      )}
    </>
  );
}

export { GameOverviewPage as default };
